import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type SessionTypeCategory = 'Service' | 'Administrative';

export interface SessionType {
    id: string;
    name: string;
    color: string;
    labelDefault: string;
    category: SessionTypeCategory;
    serviceTypeId: string | null;
    payable: boolean;
    noteTypeId: string | null;
    status: 'Active' | 'Inactive';
}

export interface SessionTypeWritePayload {
    name?: string;
    color?: string;
    labelDefault?: string;
    category?: SessionTypeCategory;
    serviceTypeId?: string | null;
    payable?: boolean;
    noteTypeId?: string | null;
    status?: 'Active' | 'Inactive';
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

function normalize(row: SessionType & { status: string }): SessionType {
    return { ...row, status: normalizeStatus(row.status) };
}

export function useSessionTypes() {
    return useQuery({
        queryKey: ['session-types'] as const,
        queryFn: async () => {
            const res = await api.get<Array<SessionType & { status: string }>>('/session-types');
            return res.data.map(normalize);
        },
        staleTime: 60_000,
    });
}

export function useCreateSessionType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: SessionTypeWritePayload) => {
            const res = await api.post('/session-types', payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['session-types'] }),
    });
}

export function useUpdateSessionType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: SessionTypeWritePayload }) => {
            const res = await api.patch(`/session-types/${id}`, payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['session-types'] }),
    });
}

export function useDeleteSessionType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/session-types/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['session-types'] }),
    });
}
